// Procedural stars for generated systems and the authored remote ones.
// The photosphere is driven by the star's temperature: blackbody colour
// (computed on the CPU, src/systems/blackbody.js), granulation whose
// cell size follows surface gravity (tight cells on dwarfs, a handful of
// giant convection cells on red supergiants), starspots on cool active
// stars, flare-bright active regions on red dwarfs, limb darkening with
// the limb reddened, and a banded mode for brown dwarfs, which are
// closer to a hot gas giant than a star.
//
// The glow is a separate camera-facing quad, built in view space in the
// vertex shader, additive, with slow corona streamers. Everything stays
// above 1.0 on purpose so the bloom pass picks the star up the way it
// does Sol.

import { OKLAB_MIX_GLSL } from './oklab.js';
import { NOISE3D_GLSL } from './noise3d.js';

export const proceduralStarVertexShader = `
  varying vec3 vLocalNormal;
  varying vec3 vViewNormal;
  varying vec3 vViewPos;
  void main() {
    vLocalNormal = normalize(normal);
    vViewNormal = normalize(normalMatrix * normal);
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    vViewPos = mv.xyz;
    gl_Position = projectionMatrix * mv;
  }
`;

export const proceduralStarFragmentShader = `
  precision highp float;
  varying vec3 vLocalNormal;
  varying vec3 vViewNormal;
  varying vec3 vViewPos;

  uniform float uTime;
  uniform vec3 uColor;       // blackbody, linear, max component 1
  uniform vec3 uCoolColor;   // blackbody ~700 K cooler (spots, lanes, limb)
  uniform vec3 uHotColor;    // blackbody ~800 K hotter (granule tops, faculae)
  uniform float uGranScale;  // granulation cell frequency on the unit sphere
  uniform float uSpots;      // 0..1 starspot coverage
  uniform float uFlare;      // 0..1 active-region brightening
  uniform float uLimb;       // linear limb darkening coefficient
  uniform float uBanded;     // 0..1 brown-dwarf banding
  uniform float uIntensity;  // overall radiance, > 1 for bloom
  uniform vec3 uSeed;

  ${OKLAB_MIX_GLSL}
  ${NOISE3D_GLSL}

  void main() {
    vec3 n = normalize(vLocalNormal);
    vec3 p = n + uSeed;
    float t = uTime;

    float footprint = max(length(fwidth(n)), 1e-5);
    float oct = clamp(log2(1.0 / (footprint * uGranScale * 0.25)), 1.0, 5.0);

    // Granulation: cellular convection, bright rising centres, dark
    // intergranular lanes, slowly boiling.
    vec3 gp = p * uGranScale + vec3(0.0, t * 0.02, t * 0.013);
    vec3 gw = gp + 0.45 * vec3(snoise(gp * 0.5 + t * 0.05), snoise(gp * 0.5 + 7.0 - t * 0.04), 0.0);
    vec2 w = worley(gw);
    // Giants have few, huge, soft cells; dwarfs fine sharp ones.
    float laneW = 0.1 + 1.2 / uGranScale;
    float gran = 1.0 - smoothstep(0.0, 0.85, w.x);
    float lanes = 1.0 - smoothstep(0.0, laneW, w.y - w.x);
    // Supergranulation / plasma flow.
    float flow = fbm(p * 2.2 + vec3(t * 0.01, 0.0, -t * 0.008), oct);
    float fine = oct > 3.0 ? fbm(gp * 3.0 + t * 0.03, 3.0) : 0.0;

    float temp = 0.5 + gran * 0.28 - lanes * 0.22 + flow * 0.18 + fine * 0.08;

    // Starspots: umbra + penumbra, clustered in activity belts.
    float belts = smoothstep(0.85, 0.2, abs(n.y * 1.8 - 0.0)) * smoothstep(0.02, 0.18, abs(n.y));
    float spotField = fbm(p * 3.3 + 40.0 + vec3(0.0, 0.0, t * 0.002), 4.0);
    float spotThr = 1.0 - uSpots * 1.1;
    float penumbra = smoothstep(spotThr - 0.02, spotThr + 0.1, spotField * (0.6 + belts * 0.6));
    float umbra = smoothstep(spotThr + 0.12, spotThr + 0.22, spotField * (0.6 + belts * 0.6));
    penumbra *= step(0.001, uSpots);
    umbra *= step(0.001, uSpots);

    // Brown dwarf bands.
    if (uBanded > 0.0) {
      float y = n.y + 0.07 * fbm(p * vec3(2.0, 0.6, 2.0) + t * 0.01, 4.0);
      float band = sin(y * 22.0 + fbm(p * 3.0, 3.0) * 1.5) * 0.5 + 0.5;
      temp = mix(temp, 0.3 + band * 0.45 + flow * 0.12, uBanded);
    }

    vec3 col = oklchMix(uCoolColor, uColor, smoothstep(0.1, 0.55, temp));
    col = oklchMix(col, uHotColor, smoothstep(0.55, 0.95, temp));
    float bright = 0.75 + temp * 0.5;
    bright *= 1.0 - penumbra * 0.45 - umbra * 0.4;
    col = oklchMix(col, uCoolColor, penumbra * 0.6);

    // Active regions: plage and flare kernels near the spots.
    float plage = smoothstep(0.35, 0.8, fbm(p * 5.0 + vec3(t * 0.02), 3.0)) * uFlare;
    float flicker = 0.7 + 0.3 * sin(t * 1.7 + flow * 9.0);
    bright += plage * flicker * 0.9 * (1.0 - umbra);

    // Limb: darken (Eddington-style linear law) and redden, with faculae
    // (bright plage) showing near the limb as on the Sun.
    float mu = clamp(dot(normalize(vViewNormal), normalize(-vViewPos)), 0.0, 1.0);
    float limb = 1.0 - uLimb * (1.0 - pow(mu, 0.9));
    float faculae = smoothstep(0.55, 0.15, mu) * smoothstep(0.2, 0.6, flow + gran * 0.3) * 0.35;
    col = oklchMix(uCoolColor, col, smoothstep(0.0, 0.55, mu));
    bright = bright * limb + faculae;

    gl_FragColor = vec4(col * bright * uIntensity, 1.0);
  }
`;

export const starGlowVertexShader = `
  uniform float uSize;
  varying vec2 vUv;
  void main() {
    vUv = position.xy;
    vec4 centre = modelViewMatrix * vec4(0.0, 0.0, 0.0, 1.0);
    // World-space size; scale from the model matrix keeps it honest if
    // the star group is ever scaled.
    float s = uSize * length(modelMatrix[0].xyz);
    centre.xy += position.xy * s;
    gl_Position = projectionMatrix * centre;
  }
`;

export const starGlowFragmentShader = `
  precision highp float;
  varying vec2 vUv;
  uniform vec3 uColor;
  uniform float uCore;      // photosphere radius as a fraction of the quad
  uniform float uStrength;
  uniform float uRays;      // corona streamer strength
  uniform float uTime;
  uniform float uSeed;

  float h1(float n) { return fract(sin(n * 91.3458 + uSeed) * 47453.5453); }
  float n1(float x) {
    float i = floor(x);
    float f = fract(x);
    return mix(h1(mod(i, 64.0)), h1(mod(i + 1.0, 64.0)), f * f * (3.0 - 2.0 * f));
  }

  void main() {
    float d = length(vUv);
    if (d > 1.0) discard;
    float a = atan(vUv.y, vUv.x) / 6.28318 + 0.5;
    float x = max(d - uCore, 0.0) / max(1.0 - uCore, 1e-4);
    // Inner corona (tight, hot) + outer halo (wide, soft).
    float inner = exp(-x * 14.0);
    float halo = pow(uCore / max(d, uCore), 2.2) * 0.35;
    float rays = n1(a * 64.0 + uTime * 0.03) * 0.6 + n1(a * 64.0 + 17.0 - uTime * 0.02) * 0.4;
    rays = pow(rays, 3.0) * exp(-x * 5.0) * uRays * 0.6;
    float glow = (inner + halo + rays) * smoothstep(1.0, 0.6, d);
    vec3 col = mix(uColor, vec3(1.0), inner * 0.35);
    gl_FragColor = vec4(col * glow * uStrength, 1.0);
  }
`;
